import {
    Box,
    Drawer,
    SvgIcon,
    createTheme,
    useMediaQuery
} from '@mui/material';
import { Logo } from './Logo';
import { useLocation } from 'react-router-dom';
import { Theme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { List, ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import DirectionsBusFilledOutlinedIcon from '@mui/icons-material/DirectionsBusFilledOutlined';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PublicIcon from '@mui/icons-material/Public';
import DashboardSharpIcon from '@mui/icons-material/DashboardSharp';
import SchoolIcon from '@mui/icons-material/School';
import MonitorIcon from '@mui/icons-material/Monitor';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AddCardIcon from '@mui/icons-material/AddCard';
import { SIDE_NAV_WIDTH } from './consts';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { useEffect, useState } from 'react';
import { PageEnum, RoleEnum } from '../../types/enums';
import { withStyles } from '@mui/styles';

/**
 * @remarks - This is the side Navigation of the YEC website and allows users to navigate to
 * all of the basic pages they have access to.
 * @param props - values passed into SideNav when called so it knows when to open or close.
 * @returns - either a List of pages available to the user on the side of the screen, or a Menu
 * containing that list which can be opened with an Icon button on smaller screens.
 */
export const SideNav = (props) => {
    const { open, onClose } = props;
    const user = useSelector((state: RootState) => state.userDetail);
    const navigate = useNavigate();
    const location = useLocation();
    let pathname = location.pathname;
    const lgUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));

    const CustomListItemButton = withStyles({
        root: {
            "&$selected": {
                backgroundColor: "#10AD61",
                color: "white",
                "& .MuiListItemIcon-root": {
                    color: "white",
                },
            },
            "&$selected:hover": {
                backgroundColor: "#0E9453",
                color: "white",
                "& .MuiListItemIcon-root": {
                    color: "white",
                },
            },
        },
        selected: {},
    })(ListItemButton);

    const onMenuItemClicked = (route: string) => {
        onClose();
        navigate(route);
    }

    const handlePageTitle = () => {
        const roleTitles = {
            1: "Admin Dashboard",
            2: "Admin Dashboard",
            3: "Credentialing Manager Dashboard",
            4: "Judge Dashboard",
            default: "Admin Dashboard"
        };
        return roleTitles[user.roleId] || roleTitles.default;
    };

    const [items, setItems] = useState<any>([
        {
            title: 'Youth Dashboard',
            path: '/youthdashboard',
            pageName: PageEnum.YouthDashboard,
            icon: (
                <SvgIcon fontSize="small">
                    <DashboardSharpIcon />
                </SvgIcon>
            )
        },
        {
            title: 'School Coordinator Dashboard',
            path: '/schoolcoordinatordashboard',
            pageName: PageEnum.SchoolCoordinatorDashboard,
            icon: (
                <SvgIcon fontSize="small">
                    <SchoolIcon />
                </SvgIcon>
            )
        },
        {
            title: handlePageTitle(),
            path: '/admindashboard',
            pageName: PageEnum.AdminDashboard,
            icon: (
                <SvgIcon fontSize="small">
                    <MonitorIcon />
                </SvgIcon>
            )
        },
        {
            title: 'User Admin',
            path: '/useradmin',
            pageName: PageEnum.UserAdmin,
            icon: (
                <SvgIcon fontSize="small">
                    <AdminPanelSettingsIcon />
                </SvgIcon>
            )
        },
        {
            title: 'School Admin',
            path: '/schooladmin',
            pageName: PageEnum.SchoolAdmin,
            icon: (
                <SvgIcon fontSize="small">
                    <DirectionsBusFilledOutlinedIcon />
                </SvgIcon>
            )
        },
        {
            title: 'Region Admin',
            path: '/regionadmin',
            pageName: PageEnum.RegionAdmin,
            icon: (
                <SvgIcon fontSize="small">
                    <PublicIcon />
                </SvgIcon>
            )
        },
        {
            title: 'Download User Data',
            path: '/downloaduserdata',
            pageName: PageEnum.DownloadUserData,
            icon: (
                <SvgIcon fontSize="small">
                    <FileDownloadIcon />
                </SvgIcon>
            )
        },
        {
            title: 'Manage Sponsored Ads',
            path: '/managesponsoredads',
            pageName: PageEnum.ManageSponsoredAds,
            icon: (
                <SvgIcon fontSize="small">
                    <AddCardIcon />
                </SvgIcon>
            )
        },
    ]);

    const content = (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%'
            }}
        >
            <Logo />
            {user.isActive && <List component="nav">
                {items.map((item) => {

                    return (
                        <ListItem onClick={() => onMenuItemClicked(item.path)} key={item.path} disablePadding sx={{ display: 'block' }} >
                            <CustomListItemButton
                                selected={pathname === item.path}
                                sx={{
                                    minHeight: 48,
                                    px: 2.5,
                                }}
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: 0,
                                        mr: 2,
                                        justifyContent: 'center',
                                    }}
                                >
                                    {item.icon}
                                </ListItemIcon>
                                <ListItemText className='menu-text' primary={item.title} sx={{ color: "#ffffff" }} />
                            </CustomListItemButton>
                        </ListItem>
                    );
                })}
            </List>}
        </Box>
    );

    useEffect(() => {
        pathname = location.pathname;
        let updatedItems;
        switch (user.roleId) {
            case RoleEnum.MasterSystemAdmin:
                updatedItems = items.filter(item => item.pageName !== PageEnum.SchoolCoordinatorDashboard && item.pageName !== PageEnum.YouthDashboard);
                setItems(updatedItems);
                break;
            case RoleEnum.SystemAdministrator:
                updatedItems = items.filter(
                    item => item.pageName !== PageEnum.SchoolCoordinatorDashboard &&
                        item.pageName !== PageEnum.YouthDashboard &&
                        item.pageName !== PageEnum.RegionAdmin &&
                        item.pageName !== PageEnum.DownloadUserData &&
                        item.pageName !== PageEnum.ManageSponsoredAds
                );
                setItems(updatedItems);
                break;
            case RoleEnum.CredentialingManager:
                updatedItems = items.filter(item => item.pageName === PageEnum.UserAdmin || item.pageName === PageEnum.AdminDashboard);
                setItems(updatedItems);
                break;
            case RoleEnum.Judge:
                updatedItems = items.filter(item => item.pageName === PageEnum.AdminDashboard);
                setItems(updatedItems);
                break;
            case RoleEnum.SchoolCoordinator:
                updatedItems = items.filter(item => item.pageName === PageEnum.SchoolCoordinatorDashboard);
                setItems(updatedItems);
                break;
            case RoleEnum.Youth:
                updatedItems = items.filter(item => item.pageName === PageEnum.YouthDashboard);
                setItems(updatedItems);
                break;
            default:
                console.error("invalid role.");
        }
    }, [user]);

    if (lgUp) {
        return (
            <Drawer
                anchor="left"
                className='side-menu-drawer'
                onClose={onClose}
                open={open}
                PaperProps={{
                    sx: {
                        backgroundColor: '#969696',
                        width: SIDE_NAV_WIDTH
                    }
                }}
                variant="permanent"
            >
                {content}
            </Drawer>
        );
    }

    return (
        <Drawer
            anchor="left"
            className='side-menu-drawer'
            onClose={onClose}
            open={open}
            PaperProps={{
                sx: {
                    backgroundColor: '#969696',
                    width: SIDE_NAV_WIDTH
                }
            }}
            sx={{ zIndex: (theme) => theme.zIndex.appBar + 100 }}
            variant="temporary"
        >
            {content}
        </Drawer>
    );
};
